import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Discovery, Chart, Heart, Calling } from 'react-iconly';

import CTA from '../components/CTA';
import { devError } from '../utils/devLog';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import BlurText from '../components/BlurText';
import profileImg from '../assets/Group 5 (1).png';
import { useTransition } from '../context/TransitionContext';
import { useLanguage } from '../context/LanguageContext';
import ScrollVideoPlayer from '../components/ScrollVideoPlayer';

import { fetchProjects } from '../services/supabaseService';
import { AwardBadge } from '../components/AwardBadge';
import Lottie from 'lottie-react';
import revenueIcon from '../assets/icons/revenue.json';

gsap.registerPlugin(ScrollTrigger);

// Component to focus/highlight text when it hits the center of the viewport
const CenterFocusText = ({ children }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 65%", "end 35%"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.15, 1, 1, 0.15]);
  
  return (
    <motion.div ref={ref} style={{ opacity, color: 'var(--v2-color-charcoal)' }}>
      {children}
    </motion.div>
  );
};

const TiltedBanner = () => {
  const ref = useRef(null);
  const { startTransition } = useTransition();
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const rotate = useTransform(scrollYProgress, [0, 1], [-3, 3]);

  return (
    <motion.div 
      ref={ref}
      className="dark-section"
      style={{ 
        backgroundColor: 'var(--v2-color-charcoal)', 
        color: 'white', 
        borderRadius: '32px', 
        padding: '100px 80px', 
        display: 'flex', 
        gap: '64px', 
        alignItems: 'center',
        flexWrap: 'wrap',
        position: 'relative',
        rotate: rotate
      }}
    >
      <div style={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(105deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 60%)',
          pointerEvents: 'none',
          borderRadius: '32px'
      }} />
      <h2 className="v2-display-l" style={{ margin: 0, flex: '1 1 300px', letterSpacing: '-0.04em', lineHeight: 0.9, position: 'relative', zIndex: 1 }}>
        {t('about.partOfEcosystem')}<br/>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{t('about.ecosystemTitle')}</span>
      </h2>
      <div style={{ flex: '1 1 300px', position: 'relative', zIndex: 1 }}>
        <p className="v2-body-m" style={{ marginBottom: '24px', fontSize: '18px' }}>
          {t('about.bio1')}
        </p>
        <p className="v2-body-m" style={{ marginBottom: '40px', color: 'rgba(255,255,255,0.6)', fontSize: '18px' }}>
          {t('about.bio2')}
        </p>
        <button 
          onClick={() => startTransition('/work')}
          style={{ 
          background: 'none', border: 'none', padding: 0, 
          display: 'flex', alignItems: 'center', gap: '12px', 
          fontFamily: 'var(--v2-font-mono)', fontSize: '12px', fontWeight: 'bold', 
          color: 'white', cursor: 'pointer' 
        }}>
          {t('about.viewMyWork')}
          <span style={{ width: '32px', height: '32px', backgroundColor: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Discovery size={16} color="var(--v2-color-charcoal)" set="curved" weight="bold" />
          </span>
        </button>
      </div>
    </motion.div>
  );
};

const About = () => {
  const { startTransition } = useTransition();
  const { t } = useLanguage();
  const wordsRef = useRef([]);
  const [totalProjects, setTotalProjects] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const projects = await fetchProjects();
        if (projects) {
          setTotalProjects(projects.length);
        }
      } catch (e) {
        devError(e);
      }
    };
    loadData();
  }, []);

  // Clear refs during render to avoid duplicates on re-render
  wordsRef.current = [];

  useEffect(() => {
    if (wordsRef.current.length === 0) return;

    gsap.fromTo(wordsRef.current,
      { opacity: 0.15, filter: 'blur(4px)' },
      {
        opacity: 1,
        filter: 'blur(0px)',
        stagger: 0.05,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".about-reveal-text",
          start: "top 75%",
          end: "bottom 35%",
          scrub: 1, // Smooth scrub effect
        }
      }
    );
  }, []);

  return (
    <div style={{ backgroundColor: 'var(--v2-color-offwhite)' }}>
      {/* 1. Result Driven Design Section */}
      <section className="v2-container" style={{ paddingTop: '160px', paddingBottom: '80px', textAlign: 'center' }}>
        <h1 className="v2-display-xl" style={{ letterSpacing: '-0.02em', lineHeight: 0.9, marginBottom: '64px' }}>
          {t('about.resultDriven') || t('about.designDriven')}
        </h1>
        
        {/* Spline 3D Asset via Iframe with CSS Hacks */}
        <div style={{ 
          width: '100%', 
          height: '500px', 
          maxWidth: '800px',
          margin: '0 auto', 
          borderRadius: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0,0,0,0.05)'
        }}>
          <iframe 
            src="https://my.spline.design/3dpathshandwrittencopy-1azR2SwlVBtIFDUR8OKnvt6Z/" 
            frameBorder="0" 
            title="3D Design"
            style={{ 
              width: '100%', 
              height: '100%', 
              pointerEvents: 'auto',
              transform: 'scale(1.4)', 
              transformOrigin: 'center center' 
            }}
          ></iframe>
        </div>
        
        <div style={{ maxWidth: '800px', margin: '120px auto 80px auto', textAlign: 'left' }}>
          <div className="about-reveal-text" style={{ fontSize: '32px', lineHeight: 1.4, letterSpacing: '-0.02em', fontFamily: 'Gellix, sans-serif', fontWeight: 500 }}>
            <p style={{ marginBottom: '32px', color: 'var(--v2-color-charcoal)' }}>
              {t('about.designDriver').split(" ").map((word, i) => (
                <span key={`p1-${i}`} ref={el => { if (el) wordsRef.current.push(el) }} style={{ display: 'inline-block', marginRight: '0.25em', opacity: 0.15, filter: 'blur(4px)', willChange: 'opacity, filter' }}>{word}</span>
              ))}
            </p>
            <p style={{ marginBottom: '32px', color: 'var(--v2-color-charcoal)' }}>
              {t('about.effectiveDesign').split(" ").map((word, i) => (
                <span key={`p2-${i}`} ref={el => { if (el) wordsRef.current.push(el) }} style={{ display: 'inline-block', marginRight: '0.25em', opacity: 0.15, filter: 'blur(4px)', willChange: 'opacity, filter' }}>{word}</span>
              ))}
            </p>
            <p style={{ marginBottom: '32px', color: 'var(--v2-color-charcoal)' }}>
              {"Alvin Ahmad".split(" ").map((word, i) => (
                <span key={`p3a-${i}`} ref={el => { if (el) wordsRef.current.push(el) }} style={{ display: 'inline-block', marginRight: '0.25em', opacity: 0.15, filter: 'blur(4px)', willChange: 'opacity, filter' }}>{word}</span>
              ))}
              <span style={{ display: 'inline-block', marginRight: '0.25em', opacity: 1, filter: 'none' }}>
                <img 
                  src={profileImg} 
                  alt="Alvin Ahmad" 
                  style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    display: 'inline-block', 
                    verticalAlign: 'middle', 
                    margin: '0 4px',
                    marginTop: '-4px',
                    objectFit: 'cover'
                  }} 
                />
              </span>
              {t('about.mission').split(" ").map((word, i) => (
                <span key={`p3b-${i}`} ref={el => { if (el) wordsRef.current.push(el) }} style={{ display: 'inline-block', marginRight: '0.25em', opacity: 0.15, filter: 'blur(4px)', willChange: 'opacity, filter' }}>{word}</span>
              ))}
            </p>
          </div>
        </div>
      </section>

      {/* 2. Customer / Client / Results Bento Grid */}
      <section className="v2-container" style={{ paddingBottom: '80px' }}>
        <div className="v2-bento-grid">
          {/* Box 1 */}
          <div style={{ backgroundColor: 'var(--v2-color-charcoal)', color: 'white', padding: '40px', borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', aspectRatio: '1.4/1' }}>
            <div>
              <h3 className="v2-heading-s">{totalProjects !== null ? totalProjects : '...'} {t('about.handlingApps')}</h3>
            </div>
            <div style={{ alignSelf: 'flex-end', textAlign: 'right' }}>
              <AwardBadge />
            </div>
          </div>

          {/* Box 2 */}
          <div style={{ backgroundImage: 'linear-gradient(to bottom right, #FF7722, #FF3300)', color: 'white', padding: '40px', borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', aspectRatio: '1.4/1', position: 'relative' }}>
            <h3 className="v2-heading-s" style={{ maxWidth: '200px', zIndex: 2 }}>{t('about.raisedRevenue')}</h3>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, pointerEvents: 'none' }}>
               <Lottie animationData={revenueIcon} loop={true} style={{ width: 150, height: 150 }} />
            </div>
            <div className="v2-display-l" style={{ alignSelf: 'flex-end', margin: 0, opacity: 0.9, zIndex: 2 }}>$1000+</div>
          </div>

          {/* Box 3 */}
          <div className="v2-bento-box-3" style={{ backgroundColor: '#FF3333', color: 'white', borderRadius: '24px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
             <video 
               src="https://ixezilyqdbarjbqfoczc.supabase.co/storage/v1/object/public/assets/growth.mp4"
               autoPlay
               loop
               muted
               playsInline
               style={{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 objectFit: 'cover',
                 zIndex: 0,
                 transform: 'scaleX(-1)'
               }}
             />
             <div className="v2-display-xl" style={{ position: 'absolute', top: '10%', left: '5%', margin: 0, opacity: 0.9, zIndex: 2, fontSize: 'clamp(32px, 5vw, 64px)', color: 'black' }}>10,000+</div>
             <div className="v2-heading-s" style={{ position: 'absolute', bottom: '40px', left: '40px', zIndex: 2, color: 'black' }}>{t('about.usersGrowth')}</div>
          </div>

          {/* Box 4 */}
          <div className="v2-bento-box-4" style={{ backgroundColor: '#1A1A1A', color: 'white', borderRadius: '24px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
             <video 
               src="https://ixezilyqdbarjbqfoczc.supabase.co/storage/v1/object/public/assets/feedback.mp4"
               autoPlay
               loop
               muted
               playsInline
               style={{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 objectFit: 'cover',
                 zIndex: 0,
               }}
             />
             <div style={{ padding: '40px', position: 'relative', zIndex: 2 }}>
               <h3 className="v2-heading-s">90% {t('about.satisfyingFeedback')}</h3>
             </div>
          </div>
        </div>
        
        {/* Trusted By Clients Section */}
        <div style={{ textAlign: 'center', margin: '160px 0' }}>
           <span className="v2-label-xs" style={{ color: 'var(--v2-color-taupe)', letterSpacing: '0.1em' }}>{t('about.techIWorkWith') ? t('about.techIWorkWith').toUpperCase() : 'TECHNOLOGIES I WORK WITH'}</span>
            <div style={{ 
              marginTop: '64px', 
              fontSize: 'clamp(3rem, 10vw, 150px)', 
              fontWeight: 900, 
              fontFamily: 'var(--v2-font-display)', 
              lineHeight: 0.9, 
              letterSpacing: '-0.04em' 
            }}>
               <CenterFocusText>React</CenterFocusText>
               <CenterFocusText>Next.js</CenterFocusText>
               <CenterFocusText>Node.js</CenterFocusText>
               <CenterFocusText>React Native</CenterFocusText>
               <CenterFocusText>Supabase</CenterFocusText>
               <CenterFocusText>Tailwind</CenterFocusText>
               <CenterFocusText>TypeScript</CenterFocusText>
            </div>
        </div>
      </section>

      {/* Video / Team Section */}
      <ScrollVideoPlayer src="https://ixezilyqdbarjbqfoczc.supabase.co/storage/v1/object/public/assets/aesthetic.mp4">
           {/* Thumbnails Strip */}
           <div style={{ 
              position: 'absolute', 
              bottom: '96px',
              left: '50%', 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              gap: '8px', 
              padding: '8px', 
              backgroundColor: 'rgba(0,0,0,0.2)', 
              backdropFilter: 'blur(10px)', 
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '16px',
              zIndex: 5
           }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{ 
                  width: '80px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  overflow: 'hidden', 
                  opacity: i === 3 ? 1 : 0.5, 
                  border: i === 3 ? '2px solid white' : 'none',
                  boxSizing: 'border-box'
                }}>
                  <img src={`https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80&sig=${i}`} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
           </div>
      </ScrollVideoPlayer>

      {/* Team / Business Motto Section */}
      <section className="v2-container" style={{ paddingBottom: '160px', paddingTop: '40px' }}>
         <div style={{ 
            fontSize: 'clamp(3rem, 10vw, 150px)', 
            fontWeight: 900, 
            fontFamily: 'var(--v2-font-display)', 
            lineHeight: 0.9, 
            letterSpacing: '-0.04em',
            color: 'var(--v2-color-charcoal)'
         }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
               <span>{t('about.cleanCode').split(' ').slice(0, 2).join(' ')}</span>
               <span>{t('about.cleanCode').split(' ').slice(2, 3).join(' ')}</span>
             </div>
             <div style={{ textAlign: 'center' }}>{t('about.cleanCode').split(' ').slice(3, 4).join(' ')}</div>
             <div style={{ textAlign: 'right' }}>{t('about.cleanCode').split(' ').slice(4).join(' ')}</div>
         </div>
         
         <div style={{ marginTop: '80px', maxWidth: '400px' }}>
            <p className="v2-body-m" style={{ color: 'var(--v2-color-charcoal)' }}>{t('about.passionateDev') || t('about.passionBio')}</p>
         </div>
      </section>

      {/* 3. Motto / Join Our Team / ClientFirst */}
      <section className="v2-container" style={{ paddingBottom: '160px' }}>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '40px', marginBottom: '160px', alignItems: 'start' }}>
          <span className="v2-label-xs" style={{ color: 'var(--v2-color-taupe)', letterSpacing: '0.1em' }}>{t('about.getInTouch')}</span>
          <div style={{ maxWidth: '480px' }}>
            <h3 className="v2-heading-m" style={{ marginBottom: '32px', fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{t('about.freelance') || t('about.freelanceAvailable')}</h3>
            <button 
              onClick={() => startTransition('/contact')}
              style={{ 
              background: 'none', border: 'none', padding: 0, 
              display: 'flex', alignItems: 'center', gap: '12px', 
              fontFamily: 'var(--v2-font-mono)', fontSize: '12px', fontWeight: 'bold', 
              cursor: 'pointer',
              color: 'var(--v2-color-charcoal)'
            }}>
              {t('about.contactMe')}
              <span style={{ width: '32px', height: '32px', backgroundColor: 'var(--v2-color-charcoal)', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                 <Calling size={16} color="white" set="curved" weight="bold" />
              </span>
            </button>
          </div>
        </div>

        {/* Part of ClientFirst Banner */}
        <TiltedBanner />
      </section>

      {/* 4. CTA (like home) */}
      <CTA />
    </div>
  );
};

export default About;
